// Central web auth module.
//
// Security model: the JWT access token lives ONLY in this module's memory, never
// in localStorage, so an XSS payload can't read a long-lived credential. The
// refresh token is held in an HttpOnly, Secure, SameSite=Strict cookie set by the
// backend (JS can't read it). Every request sends `X-Auth-Mode: cookie` +
// `credentials: "include"` so the backend uses the cookie flow.
//
// This is WEB-ONLY. The React Native app keeps its body-token flow, which the
// backend still serves when the X-Auth-Mode header is absent.

export const API_BASE = "https://api.adjusterphoto.com";

let accessToken: string | null = null;

// De-dupes concurrent refreshes: many requests can 401 at once, but only one
// /refresh call should fire; the rest await the same promise (no stampede that
// would rotate the refresh token multiple times and log the user out).
let refreshPromise: Promise<string | null> | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSession(token: string): void {
  accessToken = token;
  clearLegacyStorage();
}

export function clearSession(): void {
  accessToken = null;
  clearLegacyStorage();
}

// Decode the access-token JWT payload (for admin/email in the UI). Returns null
// if there's no token or it can't be parsed. Never trusted for authorization --
// the backend re-checks admin from the DB.
export function decodeAccessToken(): Record<string, any> | null {
  if (!accessToken) return null;
  try {
    return JSON.parse(atob(accessToken.split(".")[1]));
  } catch {
    return null;
  }
}

// Pre-cookie sessions stored tokens in localStorage. On first refresh we send the
// legacy refresh token in the body so the backend can rotate it into a cookie,
// then wipe localStorage for good. `token`/`refresh_token` are the legacy keys.
function legacyRefreshToken(): string | null {
  try {
    return localStorage.getItem("refresh_token");
  } catch {
    return null;
  }
}

function clearLegacyStorage(): void {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
  } catch {
    /* ignore storage errors */
  }
}

function redirectToLogin(): void {
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

async function doRefresh(): Promise<string | null> {
  const legacy = legacyRefreshToken();
  try {
    const res = await fetch(`${API_BASE}/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Auth-Mode": "cookie" },
      // If a legacy localStorage refresh token exists (cookie not yet set), send
      // it so the backend can migrate it to a cookie. Otherwise the cookie carries it.
      body: JSON.stringify(legacy ? { refresh_token: legacy } : {}),
    });
    if (!res.ok) {
      accessToken = null;
      clearLegacyStorage();
      return null;
    }
    const data = await res.json();
    const token = data.access_token || data.token || null;
    accessToken = token;
    // Migration complete: the refresh token now lives in the HttpOnly cookie.
    clearLegacyStorage();
    return token;
  } catch {
    accessToken = null;
    return null;
  }
}

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Run once at app startup to restore a session from the cookie (or migrate a
// legacy localStorage session). Returns true if a valid session was restored.
export async function bootstrapAuth(): Promise<boolean> {
  const token = await refreshAccessToken();
  return token !== null;
}

// fetch() wrapper that attaches the in-memory bearer token, the web auth-mode
// header, and the cookie; on a 401 it refreshes once and retries the request.
export async function authFetch(
  input: RequestInfo,
  init: RequestInit = {},
): Promise<Response> {
  const run = (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers || {});
    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("X-Auth-Mode", "cookie");
    return fetch(input, { ...init, headers, credentials: "include" });
  };

  const res = await run(accessToken);
  if (res.status !== 401) return res;

  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    redirectToLogin();
    throw new Error("Session expired");
  }
  return run(refreshed);
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Auth-Mode": "cookie" },
      body: "{}",
    });
  } catch {
    /* best-effort; clear local state regardless */
  }
  clearSession();
}
